import { useTranslation } from "react-i18next";
import { HeaderTextBlock, TypographyContainer } from "@/components/Typography";
import { InferType, object, string } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { Input } from "@/components/Input";
import { Card } from "@/components/Card";
import { Button } from "@/components/Button";
import { useEffect, useState } from "react";
import {
  useGetTeamMembers,
  usePostAddTeamMember,
  usePostUpdateTeamMember,
  useRelatedCustomers,
  useUser,
} from "@/requests/hooks/user";
import { Form } from "@/components/Form";
import { faCaretLeft } from "@fortawesome/pro-solid-svg-icons";
import { toast } from "react-toastify";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DeleteTeamMemberModal } from "@/components/Modal";
import { OWNER_ROLE_ID } from "@/utils/const";
import { useQueryClient } from "@tanstack/react-query";

let hasFormBeenSubmitted = false;

interface IProps {
  type: "edit" | "add";
}

const ManageUser = ({ type }: IProps) => {
  const { t } = useTranslation();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const {
    mutate: updateTeamMember,
    isPending: isUpdateTeamMemberPending,
    isError: isUpdateTeamMemberError,
    isSuccess: isUpdateMemberSuccess,
  } = usePostUpdateTeamMember();
  const {
    mutate: addTeamMember,
    isPending: isAddTeamMemberPending,
    isError: isAddTeamMemberError,
    isSuccess: isAddMemberSuccess,
  } = usePostAddTeamMember();
  const { data: user } = useUser();
  const { data: customers } = useRelatedCustomers();

  const getCurrentCustomer = () =>
    customers?.results?.find(
      customer => customer.merchantId === user?.merchantId,
    );
  const { data: teamMembers } = useGetTeamMembers({
    enabled: !!user?.merchantId && !!customers?.results?.length,
    params: {
      merchantId: user?.merchantId?.toString(),
      customerPublicKey: getCurrentCustomer()?.publicKey,
    },
  });

  const getUserToEdit = () =>
    teamMembers?.results?.find(
      teamMember => teamMember?.id?.toString() === searchParams.get("id"),
    );

  const formSchema = object({
    email: string()
      .email(t("settings.userManagement.common.form.email.errors.workEmail"))
      .required(t("settings.userManagement.common.form.email.errors.empty")),
    firstName: string().required(
      t("settings.userManagement.common.form.firstName.empty"),
    ),
    lastName: string().required(
      t("settings.userManagement.common.form.lastName.empty"),
    ),
  });

  type IUpdateTeamMemberForm = InferType<typeof formSchema>;

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    getValues,
    trigger,
  } = useForm<IUpdateTeamMemberForm>({
    resolver: yupResolver(formSchema),
  });

  useEffect(() => {
    const user = getUserToEdit();
    if (user && type === "edit") {
      setValue("email", user.username);
      setValue("firstName", user.firstName);
      setValue("lastName", user.lastName);
    }
  }, [teamMembers]);

  useEffect(() => {
    if (hasFormBeenSubmitted) {
      trigger();
    }
  }, [watch("email"), watch("firstName"), watch("lastName")]);

  useEffect(() => {
    if (isAddMemberSuccess || isUpdateMemberSuccess) {
      queryClient.removeQueries({ queryKey: ["settings", "team-members"] });
      toast.success(t(`settings.userManagement.${type}.form.success`));
      navigate("/profile/user-management");
    }
  }, [isAddMemberSuccess, isUpdateMemberSuccess]);

  if (isUpdateTeamMemberError || isAddTeamMemberError)
    toast.error(t(`settings.userManagement.${type}.form.error`));

  const submitForm = ({
    firstName,
    lastName,
    email,
  }: IUpdateTeamMemberForm) => {
    if (!hasFormBeenSubmitted) hasFormBeenSubmitted = true;

    const requestBody = {
      firstName,
      lastName,
      email,
      role: OWNER_ROLE_ID,
      customerPublicKey: [getCurrentCustomer()?.publicKey],
    };

    if (type === "edit") updateTeamMember(requestBody);
    else addTeamMember(requestBody);
  };

  const toggleDeleteModal = () => setIsDeleteModalOpen(isOpen => !isOpen);

  const renderHeaderTextBlock = () => (
    <TypographyContainer>
      <HeaderTextBlock
        align="left"
        featureText={t("settings.userManagement.header.feature")}
        descriptionTextColor="secondary"
        headingText={t(`settings.userManagement.${type}.heading`)}
        descriptionText={t(`settings.userManagement.${type}.description`)}
      />
    </TypographyContainer>
  );

  const renderBackButton = () => (
    <div className="mt-30">
      <Button
        as="link"
        variant="link"
        icon={faCaretLeft}
        iconPosition="start"
        iconClassName="h-20 w-20"
        to="/settings/user-management">
        {t("settings.userManagement.backButton")}
      </Button>
    </div>
  );

  const renderFormInputs = () => (
    <Card additionalClassName="mt-20 p-20">
      <Form>
        <div>
          <Input
            label={t("settings.userManagement.common.form.email.label")}
            placeholder={t(
              "settings.userManagement.common.form.email.placeholder",
            )}
            type="email"
            role="email"
            {...register("email")}
            helperText={errors?.email?.message}
            variant={!!errors?.email?.message ? "error" : undefined}
            helperTextTestId="email-helper-text"
            disabled={type === "edit"}
          />
        </div>
        <div>
          <Input
            label={t("settings.userManagement.common.form.firstName.label")}
            placeholder={t(
              "settings.userManagement.common.form.firstName.placeholder",
            )}
            type="text"
            role="textbox"
            {...register("firstName")}
            helperText={errors?.firstName?.message}
            variant={!!errors?.firstName?.message ? "error" : undefined}
          />
        </div>
        <div>
          <Input
            label={t("settings.userManagement.common.form.lastName.label")}
            placeholder={t(
              "settings.userManagement.common.form.lastName.placeholder",
            )}
            type="text"
            role="textbox"
            {...register("lastName")}
            helperText={errors?.lastName?.message}
            variant={!!errors?.lastName?.message ? "error" : undefined}
          />
        </div>
        <div className="mt-10 flex gap-20">
          <Button
            as="button"
            variant="primary"
            type="submit"
            onClick={handleSubmit(submitForm)}
            loading={isUpdateTeamMemberPending || isAddTeamMemberPending}>
            {t(`settings.userManagement.${type}.submit`)}
          </Button>

          {type === "edit" && (
            <Button
              as="button"
              variant="secondary-outline"
              type="button"
              onClick={toggleDeleteModal}>
              {t("settings.userManagement.edit.remove")}
            </Button>
          )}
        </div>
      </Form>
    </Card>
  );

  const renderDeleteTeamMemberModal = () => (
    <DeleteTeamMemberModal
      email={getValues("email")}
      isOpen={isDeleteModalOpen}
      closeModal={toggleDeleteModal}
      publicKey={getCurrentCustomer()?.publicKey}
    />
  );

  return (
    <>
      {renderHeaderTextBlock()}
      {renderBackButton()}
      {renderFormInputs()}

      {renderDeleteTeamMemberModal()}
    </>
  );
};

export default ManageUser;
